import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, useTheme,
  Tooltip, Dialog, DialogTitle, DialogContent
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import {
  AutoGraph, CheckCircle, School, TrendingUp, PlayArrow,
  Timeline, Psychology, AutoStories, HomeOutlined
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAttempts, getPlans, calculateMasteryMetrics } from '../utils/storage';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAttempts: 0,
    masteredVerses: 0,
    attemptedVerses: 0,
    averageAccuracy: 0,
  });
  const [learningAnalytics, setLearningAnalytics] = useState({
    overallConsistency: 0,
    overallProgression: 0,
    recentTrend: 'stable',
    learningCurve: 'plateauing',
    displayLevelEffectiveness: {},
    perfectAttempts: 0,
    averageMasteryScore: 0
  });
  const [progress, setProgress] = useState([]);
  const [mostRecentPlan, setMostRecentPlan] = useState(null);
  const [mostRecentVerse, setMostRecentVerse] = useState(null);
  const [trendModalOpen, setTrendModalOpen] = useState(false);
  const [curveModalOpen, setCurveModalOpen] = useState(false);
  const [trendChartData, setTrendChartData] = useState([]);
  const [curveChartData, setCurveChartData] = useState([]);

  useEffect(() => {
    const calculateDashboardData = () => {
      const attempts = getAttempts();
      const plans = getPlans();

      // Calculate stats similar to Progress component
      const verseAttempts = {};
      attempts.forEach(attempt => {
        if (!verseAttempts[attempt.verseId]) {
          verseAttempts[attempt.verseId] = [];
        }
        verseAttempts[attempt.verseId].push(attempt);
      });

      const progress = Object.keys(verseAttempts).map(verseId => {
        const atts = verseAttempts[verseId].sort((a, b) => a.timestamp - b.timestamp);
        const masteryMetrics = calculateMasteryMetrics(atts);
        const averageAccuracy = atts.reduce((sum, attempt) => sum + attempt.accuracy, 0) / atts.length;

        return {
          verseId,
          reference: verseId,
          attempts: atts.length,
          // New patent-aligned mastery metrics
          masteryScore: masteryMetrics.score,
          masteryLevel: masteryMetrics.level,
          masteryLevelLabel: masteryMetrics.levelLabel,
          masteryLevelColor: masteryMetrics.levelColor,
          isMastered: masteryMetrics.isMastered,
          accuracy: masteryMetrics.accuracy,
          consistency: masteryMetrics.consistency,
          progression: masteryMetrics.progression,
          // Keep backward compatibility with some old metrics
          mastery: masteryMetrics.score, // Keep this for display purposes
          averageAccuracy: Math.round(averageAccuracy),
          lastAttempt: atts[atts.length - 1],
        };
      });

      // Find most recent verse
      const mostRecentVerse = progress
        .filter(p => p.lastAttempt)
        .sort((a, b) => b.lastAttempt.timestamp - a.lastAttempt.timestamp)[0];

      // Find most recent plan (by last access or creation)
      const mostRecentPlan = plans
        .sort((a, b) => (b.lastAccessed || b.created || 0) - (a.lastAccessed || a.created || 0))[0];

      setProgress(progress);
      setMostRecentVerse(mostRecentVerse || null);
      setMostRecentPlan(mostRecentPlan || null);

      const totalAttempts = attempts.length;
      const masteredCount = progress.filter(p => p.isMastered).length;
      const attemptedCount = progress.filter(p => p.attempts > 0).length;
      const overallAverage = progress.length > 0
        ? Math.round(progress.reduce((sum, p) => sum + p.averageAccuracy, 0) / progress.length)
        : 0;

      setStats({
        totalAttempts,
        masteredVerses: masteredCount,
        attemptedVerses: attemptedCount,
        averageAccuracy: overallAverage,
      });

      // Calculate detailed learning analytics
      if (progress.length > 0) {
        const averageMasteryScore = progress.reduce((sum, p) => sum + p.masteryScore, 0) / progress.length;
        const averageConsistency = progress.reduce((sum, p) => sum + p.consistency, 0) / progress.length;
        const averageProgression = progress.reduce((sum, p) => sum + p.progression, 0) / progress.length;
        const perfectAttempts = progress.reduce((sum, p) => sum + (p.details ? p.details.perfectAttempts : 0), 0);
        const trendingUp = progress.filter(p => p.progression > 60).length;
        const trend = trendingUp > progress.length / 2 ? 'improving' :
                     progress.filter(p => p.progression < 40).length > progress.length / 2 ? 'declining' : 'stable';

        // Determine learning curve based on overall progression
        const learningCurve = averageProgression > 70 ? 'growing' :
                             averageMasteryScore > 70 ? 'mature' :
                             averageProgression < 35 ? 'stagnating' : 'plateauing';

        setLearningAnalytics({
          overallConsistency: Math.round(averageConsistency),
          overallProgression: Math.round(averageProgression),
          recentTrend: trend,
          learningCurve,
          perfectAttempts,
          averageMasteryScore: Math.round(averageMasteryScore)
        });

        // Calculate initial chart data for display
        prepareTrendChartData();
        prepareCurveChartData();
      }
    };

    calculateDashboardData();
  }, []);

  const prepareTrendChartData = () => {
    const attempts = getAttempts()
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 20) // Last 20 attempts
      .reverse(); // Chronological order

    const data = attempts.map((attempt, index) => ({
      attempt: index + 1,
      accuracy: attempt.accuracy,
      verse: attempt.verseId,
    }));

    setTrendChartData(data);
  };

  const prepareCurveChartData = () => {
    const verseAttempts = {};
    const attempts = getAttempts().sort((a, b) => a.timestamp - b.timestamp);

    attempts.forEach(attempt => {
      if (!verseAttempts[attempt.verseId]) {
        verseAttempts[attempt.verseId] = [];
      }
      verseAttempts[attempt.verseId].push(attempt);
    });

    const data = attempts.map((attempt, index) => {
      // Calculate rolling mastery for all verses up to this attempt
      const currentAttempts = attempts.slice(0, index + 1);
      const currentVerseAttempts = {};

      currentAttempts.forEach(att => {
        if (!currentVerseAttempts[att.verseId]) {
          currentVerseAttempts[att.verseId] = [];
        }
        currentVerseAttempts[att.verseId].push(att);
      });

      const currentProgress = Object.keys(currentVerseAttempts).map(verseId => {
        const masteryMetrics = calculateMasteryMetrics(currentVerseAttempts[verseId]);
        return {
          masteryScore: masteryMetrics.score,
        };
      });

      const averageMastery = currentProgress.length > 0
        ? currentProgress.reduce((sum, p) => sum + p.masteryScore, 0) / currentProgress.length
        : 0;

      return {
        point: index + 1,
        mastery: Math.round(averageMastery),
        timestamp: new Date(attempt.timestamp).toLocaleDateString(),
      };
    });

    setCurveChartData(data);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return theme.palette.success.main;
    if (accuracy >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };



  const handleTrendClick = () => {
    // Prepare trend chart data - recent accuracy attempts
    const attempts = getAttempts()
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 20) // Last 20 attempts
      .reverse(); // Chronological order

    const data = attempts.map((attempt, index) => ({
      attempt: index + 1,
      accuracy: attempt.accuracy,
      verse: attempt.verseId,
    }));

    setTrendChartData(data);
    setTrendModalOpen(true);
  };

  const handleCurveClick = () => {
    // Prepare curve chart data - mastery progression over time
    const verseAttempts = {};
    const attempts = getAttempts().sort((a, b) => a.timestamp - b.timestamp);

    attempts.forEach(attempt => {
      if (!verseAttempts[attempt.verseId]) {
        verseAttempts[attempt.verseId] = [];
      }
      verseAttempts[attempt.verseId].push(attempt);
    });

    const data = attempts.map((attempt, index) => {
      // Calculate rolling mastery for all verses up to this attempt
      const currentAttempts = attempts.slice(0, index + 1);
      const currentVerseAttempts = {};

      currentAttempts.forEach(att => {
        if (!currentVerseAttempts[att.verseId]) {
          currentVerseAttempts[att.verseId] = [];
        }
        currentVerseAttempts[att.verseId].push(att);
      });

      const currentProgress = Object.keys(currentVerseAttempts).map(verseId => {
        const masteryMetrics = calculateMasteryMetrics(currentVerseAttempts[verseId]);
        return {
          masteryScore: masteryMetrics.score,
        };
      });

      const averageMastery = currentProgress.length > 0
        ? currentProgress.reduce((sum, p) => sum + p.masteryScore, 0) / currentProgress.length
        : 0;

      return {
        point: index + 1,
        mastery: Math.round(averageMastery),
        timestamp: new Date(attempt.timestamp).toLocaleDateString(),
      };
    });

    setCurveChartData(data);
    setCurveModalOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Card
        sx={{
          p: 4,
          mb: 4,
          borderRadius: theme.shape.borderRadius * 2,
          backgroundColor: 'surfaceContainerHighest',
          border: `1px solid ${theme.palette.outline.variant}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HomeOutlined sx={{ mr: 2, fontSize: 40, color: theme.palette.primary.main }} />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                mb: 1
              }}
            >
              Your Bible Memory Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Welcome back! Here are your key metrics and active learning activities
            </Typography>
          </Box>
        </Box>
      </Card>



      {/* Learning Metrics Dashboard */}
            <Card sx={{
              mb: 4,
              borderRadius: theme.shape.borderRadius * 2,
              background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
              border: `1px solid ${theme.palette.outline.light}`,
              boxShadow: theme.shadows[1]
            }}>
              <CardContent sx={{
                p: 4
              }}>

                {/* Primary Metrics */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 4, flexWrap: 'nowrap', alignItems: 'stretch' }}>
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Mastery Achievement
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Verses where your {learningAnalytics.averageMasteryScore}/100 mastery score exceeded the threshold.
                          Mastery combines accuracy (60%), consistency (25%), and improvement (15%) factors.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                        borderRadius: theme.shape.borderRadius * 2,
                        background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                        border: `1px solid ${theme.palette.outline.light}`,
                        boxShadow: theme.shadows[1],
                        height: '100%',
                        flex: '1'
                      }}>
                      <CardContent sx={{
                          textAlign: 'center',
                          p: 3
                        }}>
                          <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                            {stats.masteredVerses}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                            <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                textAlign: 'center',
                                overflowWrap: 'break-word'
                              }}
                            >
                              Mastered Verses
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                  </Tooltip>

                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Recall Performance (60% of Mastery)
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Average word accuracy across attempts. Combined with consistency (25%) and progression (15%)
                          creates your overall mastery score of {learningAnalytics.averageMasteryScore}/100.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                        borderRadius: theme.shape.borderRadius * 2,
                        background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                        border: `1px solid ${theme.palette.outline.light}`,
                        boxShadow: theme.shadows[1],
                        height: '100%',
                        flex: '1'
                      }}>
                      <CardContent sx={{
                          textAlign: 'center',
                          p: 3
                        }}>
                          <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                            {stats.averageAccuracy}%
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                            <TrendingUp sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 600,
                                textAlign: 'center',
                                overflowWrap: 'break-word'
                              }}
                            >
                              Average Accuracy
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                  </Tooltip>

                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Verses With Practice History
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            Total verses you've practiced from both your verses and plan activities.
                            These represent your complete memorization journey across all plans.
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Card sx={{
                          borderRadius: theme.shape.borderRadius * 2,
                          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                          border: `1px solid ${theme.palette.outline.light}`,
                          boxShadow: theme.shadows[1],
                          height: '100%',
                          flex: '1'
                        }}>
                        <CardContent sx={{
                            textAlign: 'center',
                            p: 3
                          }}>
                            <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                              {stats.attemptedVerses}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                              <School sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontWeight: 600,
                                  textAlign: 'center',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                Attempted Verses
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                    </Tooltip>
                </Box>

                {/* Detailed Analytics Metrics */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
                  {/* Mastery Score Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Overall Mastery Score (60% Accuracy + 25% Consistency + 15% Progression)
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Your comprehensive mastery combines three weighted factors.
                          Scores above 80% indicate you have mastered the material and can confidently recite the verses.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      minWidth: 200,
                      flex: '1'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: 3
                      }}>
                        <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                          {learningAnalytics.averageMasteryScore}%
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                          <AutoGraph sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              textAlign: 'center',
                              overflowWrap: 'break-word'
                            }}
                          >
                            Mastery Score
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>

                  {/* Perfect Attempts Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Perfect Recitations
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Number of times you achieved 100% accuracy on a verse attempt.
                          These indicate peak performance moments where you recited without errors.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      minWidth: 200,
                      flex: '1'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: 3
                      }}>
                        <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                          {learningAnalytics.perfectAttempts}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                          <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              textAlign: 'center',
                              overflowWrap: 'break-word'
                            }}
                          >
                            Perfect Attempts
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>

                  {/* Consistency Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Performance Stability (25% of Mastery)
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Measures the reliability of your verse recall performance across multiple attempts.
                          Higher consistency indicates steady learning progress without significant fluctuations.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      minWidth: 200,
                      flex: '1'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: 3
                      }}>
                        <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                          {learningAnalytics.overallConsistency}%
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                          <Timeline sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              textAlign: 'center',
                              overflowWrap: 'break-word'
                            }}
                          >
                            Consistency
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>

                  {/* Progression Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Learning Improvement (15% of Mastery)
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Tracks your improvement rate over time. High progression indicates growing expertise,
                          while stagnation may signal the need for more consistent practice or different study techniques.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      minWidth: 200,
                      flex: '1'
                    }}>
                      <CardContent sx={{
                        textAlign: 'center',
                        p: 3
                      }}>
                        <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, mb: 1 }}>
                          {learningAnalytics.overallProgression}%
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, px: 1 }}>
                          <TrendingUp sx={{ fontSize: 18, color: theme.palette.success.main }} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              textAlign: 'center',
                              overflowWrap: 'break-word'
                            }}
                          >
                            Progression
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Box>

                {/* Interactive Learning Visualizations */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                  {/* Learning Trend Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Performance Trend Analysis
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Click to view a detailed chart of your recent accuracy performance over the last 20 attempts.
                          This analysis helps identify patterns in your memorization progress and learning consistency.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: theme.shadows[2],
                      },
                      minWidth: 350,
                      flex: '1',
                      minHeight: 280
                    }} onClick={handleTrendClick}>
                      <CardContent sx={{ p: theme.spacing(2, 1, 1, 1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                          <Timeline sx={{ color: getAccuracyColor(learningAnalytics.overallProgression), fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            Learning Trend
                          </Typography>
                        </Box>
                        <Box sx={{ height: 220, mt: 1 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendChartData.slice(-20)}
                              margin={{ top: 10, right: 30, left: 20, bottom: 25 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <YAxis
                                domain={[0, 100]}
                                label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft', textAnchor: 'middle' }}
                                tick={{ fontSize: 12 }}
                                ticks={Array.from({length: 21}, (_, i) => i * 5)}
                              />
                              <XAxis
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value, index) => index + 1}
                                interval={0}
                                label={{ value: 'Last 20 Attempts', position: 'insideBottom', offset: -10, textAnchor: 'middle' }}
                              />
                              <RechartsTooltip
                                formatter={(value) => [`${value}%`, 'Accuracy']}
                                labelFormatter={() => ''}
                              />
                              <Line
                                type="monotone"
                                dataKey="accuracy"
                                stroke={getAccuracyColor(learningAnalytics.overallProgression === 'improving' ? 75 : learningAnalytics.overallProgression === 'declining' ? 45 : 60)}
                                strokeWidth={2}
                                dot={{ fill: getAccuracyColor(learningAnalytics.overallProgression === 'improving' ? 75 : learningAnalytics.overallProgression === 'declining' ? 45 : 60), strokeWidth: 2, r: 4 }}
                                connectNulls={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>

                  {/* Learning Curve Card */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Mastery Progression Chart
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          Click to explore how your mastery score has evolved over time.
                          This visualization shows your learning journey and helps identify optimal study periods.
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                  >
                    <Card sx={{
                      borderRadius: theme.shape.borderRadius * 2,
                      background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
                      border: `1px solid ${theme.palette.outline.light}`,
                      boxShadow: theme.shadows[1],
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: theme.shadows[2],
                      },
                      minWidth: 350,
                      flex: '1',
                      minHeight: 280
                    }} onClick={handleCurveClick}>
                      <CardContent sx={{ p: theme.spacing(2, 1, 1, 1) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                          <Psychology sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                          <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                            Learning Curve
                          </Typography>
                        </Box>
                        <Box sx={{ height: 220, mt: 1 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={curveChartData}
                              margin={{ top: 10, right: 30, left: 20, bottom: 25 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <YAxis
                                domain={[0, 100]}
                                label={{ value: 'Mastery %', angle: -90, position: 'insideLeft', textAnchor: 'middle' }}
                                tick={{ fontSize: 12 }}
                                ticks={Array.from({length: 21}, (_, i) => i * 5)}
                              />
                              <XAxis
                                tick={{ fontSize: 12 }}
                                tickFormatter={() => ''}
                                interval={0}
                                label={{ value: 'All Time Progression', position: 'insideBottom', offset: -10, textAnchor: 'middle' }}
                              />
                              <RechartsTooltip
                                formatter={(value) => [`${value}%`, 'Mastery']}
                                labelFormatter={() => ''}
                              />
                              <Line
                                type="monotone"
                                dataKey="mastery"
                                stroke={theme.palette.info.main}
                                strokeWidth={2}
                                dot={{ fill: theme.palette.info.main, strokeWidth: 2, r: 4 }}
                                connectNulls={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Box>
          </CardContent>
        </Card>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        {mostRecentPlan && (
          <Card
            sx={{
              borderRadius: theme.shape.borderRadius * 2,
              background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
              border: `1px solid ${theme.palette.outline.light}`,
              boxShadow: theme.shadows[1],
              minWidth: 280,
              flex: '1',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: theme.shadows[2],
              }
            }}
            onClick={() => navigate('/plans')}
          >
            {(() => {
              // Calculate plan progress stats
              const planVerses = mostRecentPlan.verses || [];
              const planProgress = planVerses.map(verseId =>
                progress.find(p => p.verseId === verseId)
              ).filter(Boolean);

              const masteredCount = planProgress.filter(p => p.isMastered).length;
              const averageAccuracy = planProgress.length > 0
                ? Math.round(planProgress.reduce((sum, p) => sum + p.averageAccuracy, 0) / planProgress.length)
                : 0;

              const totalAttempts = planProgress.reduce((sum, p) => sum + p.attempts, 0);
              const overallMasteryScore = planProgress.length > 0
                ? Math.round(planProgress.reduce((sum, p) => sum + p.masteryScore, 0) / planProgress.length)
                : 0;

              // Determine plan progress level
              let progressLabel = 'Starting';
              let progressColor = theme.palette.grey[500];
              if (masteredCount === planVerses.length && planVerses.length > 0) {
                progressLabel = 'Complete';
                progressColor = theme.palette.success.main;
              } else if (masteredCount > 0) {
                progressLabel = 'In Progress';
                progressColor = theme.palette.primary.main;
              }

              return (
                <CardContent sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                    <PlayArrow sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                      Recent Plan
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                      {mostRecentPlan.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={progressLabel}
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${progressColor}20`,
                        color: progressColor,
                        fontWeight: 600,
                        border: `1px solid ${progressColor}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Chip
                      label={`Accuracy: ${averageAccuracy}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Mastery: ${overallMasteryScore}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${overallMasteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: overallMasteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${overallMasteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Attempts: ${totalAttempts}`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: theme.palette.grey[400] + '20',
                        color: theme.palette.grey[700],
                        fontWeight: 600,
                        border: `1px solid ${theme.palette.grey[400]}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent card click
                      navigate(`/plan/${mostRecentPlan.id}`);
                    }}
                    sx={{
                      borderRadius: theme.shape.borderRadius,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Resume Plan
                  </Button>
                </CardContent>
              );
            })()}
          </Card>
        )}

        {mostRecentVerse && (
          <Card
            sx={{
              borderRadius: theme.shape.borderRadius * 2,
              background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.surfaceVariant} 100%)`,
              border: `1px solid ${theme.palette.outline.light}`,
              boxShadow: theme.shadows[1],
              minWidth: 280,
              flex: '1',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: theme.shadows[2],
              }
            }}
            onClick={() => navigate('/progress')}
          >
            <CardContent sx={{
              p: 3,
              height: '100%'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                <AutoStories sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                  Recent Verse
                </Typography>
              </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                      {mostRecentVerse.reference}
                    </Typography>
                    <Chip
                      size="small"
                      label={mostRecentVerse.masteryLevelLabel || 'Novice'}
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    <Chip
                      label={`Accuracy: ${mostRecentVerse.averageAccuracy}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${mostRecentVerse.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: mostRecentVerse.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${mostRecentVerse.averageAccuracy >= 70 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Mastery: ${mostRecentVerse.masteryScore}%`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: `${mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}20`,
                        color: mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main,
                        fontWeight: 600,
                        border: `1px solid ${mostRecentVerse.masteryScore >= 80 ? theme.palette.success.main : theme.palette.primary.main}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                    <Chip
                      label={`Attempts: ${mostRecentVerse.attempts}`}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        height: 22,
                        backgroundColor: theme.palette.grey[400] + '20',
                        color: theme.palette.grey[700],
                        fontWeight: 600,
                        border: `1px solid ${theme.palette.grey[400]}30`,
                        '& .MuiChip-label': {
                          px: 1,
                        }
                      }}
                    />
                  </Box>
              <Button
                size="small"
                variant="outlined"
                sx={{ borderRadius: theme.shape.borderRadius }}
              >
                Memorize
              </Button>
            </CardContent>
          </Card>
        )}

        {!mostRecentPlan && !mostRecentVerse && (
          <Box sx={{ textAlign: 'center', width: '100%', py: 4 }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              Start practicing to see your most recent activities here!
            </Typography>
          </Box>
        )}
      </Box>

      {/* Trend Chart Modal */}
      <Dialog
        open={trendModalOpen}
        onClose={() => setTrendModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Timeline sx={{ color: theme.palette.primary.main }} />
            Learning Trend: Recent Accuracy Attempts
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            {`This chart shows your accuracy performance over your last 20 attempts, numbered chronologically.
            The trend classification shown on your dashboard card is calculated by analyzing which verses are improving (>60% progression) vs declining (<40% progression).`}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="attempt"
                label={{ value: 'Attempt Number', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                domain={[0, 100]}
                label={{ value: 'Accuracy %', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip
                formatter={(value, name) => [`${value}%`, 'Accuracy']}
                labelFormatter={(label) => `Attempt ${label}`}
              />
              <Line
                type="monotone"
                dataKey="accuracy"
                stroke={theme.palette.primary.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>

      {/* Curve Chart Modal */}
      <Dialog
        open={curveModalOpen}
        onClose={() => setCurveModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ color: theme.palette.info.main }} />
            Learning Curve: Mastery Score Progression
          </Box>
        </DialogTitle>
        <DialogContent sx={{ minHeight: 400 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            {`This chart shows how your overall mastery score has progressed over time as you've made attempts.
            Each point represents the average mastery score across all your verses at that point in your learning journey.
            The curve classification is determined by thresholds: Growing (progression >70%), Mature (mastery >70%), Stagnating (progression <35%), or Plateaued (otherwise).`}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={curveChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="point"
                label={{ value: 'Learning Point', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                domain={[0, 100]}
                label={{ value: 'Mastery Score %', angle: -90, position: 'insideLeft' }}
              />
              <RechartsTooltip
                formatter={(value, name) => [`${value}%`, 'Mastery']}
                labelFormatter={(label) => `Point ${label}`}
              />
              <Line
                type="monotone"
                dataKey="mastery"
                stroke={theme.palette.info.main}
                strokeWidth={2}
                dot={{ fill: theme.palette.info.main, strokeWidth: 2, r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Home;
